'use client';

import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { tv } from 'tailwind-variants';

import { faChevronDown, faChevronRight, faChevronUp } from '@icons/classic/regular';
import { Label } from '../typography';
import { Badge } from './Badge';
import { Checkbox } from './Checkbox';
import { FlexContainer } from './FlexContainer';
import { input } from './Input';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';

interface MultiSelectOption {
  id: string;
  label: string;
  value: string;
}

interface MultiSelectGroup {
  id: string;
  label: string;
  options?: MultiSelectOption[];
}

interface MultiSelectWithGroupsProps {
  groups: MultiSelectGroup[];
  selectedValues: { [groupId: string]: string[] };
  onSelectionChange: (selectedValues: { [groupId: string]: string[] }) => void;
  placeholder?: string;
  className?: string;
  maxDisplayCount?: number;
}

const multiSelectTrigger = tv({
  extend: input,
  base: `
    w-fit
    min-w-48
    items-center
    justify-between
    cursor-pointer
    data-[state=open]:ring-1
    data-[state=open]:ring-primary
    data-[state=open]:ring-inset
  `,
});

// TODO: merge this and the MultipleSelector component
function MultiSelectWithGroups({
  groups,
  selectedValues,
  onSelectionChange,
  placeholder = 'Select options...',
  className,
  maxDisplayCount = 2,
}: MultiSelectWithGroupsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const getGroupsWithSelections = () => {
    const result = new Set<string>();
    for (const group of groups) {
      if (group.options && group.options.length > 0) {
        const groupSelections = selectedValues[group.id] || [];
        if (groupSelections.length > 0) {
          result.add(group.id);
        }
      }
    }
    return result;
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Expand groups that have selections each time the popover opens
      setExpandedGroups((prev) => {
        const withSelections = getGroupsWithSelections();
        if (withSelections.size === 0 && prev.size === 0) return prev;
        return new Set([...prev, ...withSelections]);
      });
    }
    setIsOpen(open);
  };

  const toggleGroupExpanded = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Get all options from groups that have options
  const allOptions = groups
    .filter((group) => group.options && group.options.length > 0)
    .flatMap((group) => group.options!);

  // Get selected option labels and group labels
  const selectedOptions = allOptions.filter((option) => {
    const group = groups.find((g) => g.options && g.options.some((opt) => opt.id === option.id));
    if (!group) return false;
    const groupSelections = selectedValues[group.id] || [];
    return groupSelections.includes(option.value);
  });

  const selectedGroups = groups
    .filter((group) => !group.options || group.options.length === 0)
    .filter((group) => {
      const groupSelections = selectedValues[group.id] || [];
      return groupSelections.length > 0;
    });

  // Create display content for trigger
  const getDisplayContent = () => {
    const totalSelected = selectedOptions.length + selectedGroups.length;

    if (totalSelected === 0) {
      return <span className="text-muted-foreground">{placeholder}</span>;
    }

    // Create selected items with group context for options
    const selectedOptionsWithGroup = selectedOptions.map((option) => {
      const group = groups.find((g) => g.options && g.options.some((opt) => opt.id === option.id));
      return {
        id: option.id,
        label: group ? `${group.label}: ${option.label}` : option.label,
      };
    });

    const allSelectedItems = [
      ...selectedOptionsWithGroup,
      ...selectedGroups.map((group) => ({ id: group.id, label: group.label })),
    ];

    const itemsToShow = allSelectedItems.slice(0, maxDisplayCount);
    const remainingCount = totalSelected - maxDisplayCount;

    return (
      <FlexContainer className="flex-wrap gap-1" items="center">
        {itemsToShow.map((item) => (
          <Badge key={item.id} variant="option">
            {item.label}
          </Badge>
        ))}
        {remainingCount > 0 && <Badge variant="option">+{remainingCount}</Badge>}
      </FlexContainer>
    );
  };

  // Handle individual option selection
  const handleOptionToggle = (optionValue: string, groupId: string) => {
    const currentGroupSelections = selectedValues[groupId] || [];
    const newGroupSelections = currentGroupSelections.includes(optionValue)
      ? currentGroupSelections.filter((value) => value !== optionValue)
      : [...currentGroupSelections, optionValue];

    const newSelectedValues = {
      ...selectedValues,
      [groupId]: newGroupSelections,
    };

    // Clean up empty arrays
    if (newGroupSelections.length === 0) {
      delete newSelectedValues[groupId];
    }

    onSelectionChange(newSelectedValues);
  };

  // Handle group selection (select/deselect all in group, or toggle standalone group)
  const handleGroupToggle = (group: MultiSelectGroup) => {
    if (!group.options || group.options.length === 0) {
      // Handle standalone group selection
      const currentGroupSelections = selectedValues[group.id] || [];
      const newSelectedValues = { ...selectedValues };

      if (currentGroupSelections.length > 0) {
        delete newSelectedValues[group.id];
      } else {
        newSelectedValues[group.id] = ['selected'];
      }

      onSelectionChange(newSelectedValues);
      return;
    }

    const groupValues = group.options.map((option) => option.value);
    const currentGroupSelections = selectedValues[group.id] || [];
    const allGroupSelected = groupValues.every((value) => currentGroupSelections.includes(value));

    const newSelectedValues = { ...selectedValues };

    if (allGroupSelected) {
      // Deselect all in group
      delete newSelectedValues[group.id];
    } else {
      // Select all in group
      newSelectedValues[group.id] = groupValues;
    }

    onSelectionChange(newSelectedValues);
  };

  // Check if group is partially or fully selected
  const getGroupSelectionState = (group: MultiSelectGroup) => {
    const currentGroupSelections = selectedValues[group.id] || [];

    if (!group.options || group.options.length === 0) {
      // For standalone groups, just check if there are any selections
      return currentGroupSelections.length > 0 ? 'all' : 'none';
    }

    const groupValues = group.options.map((option) => option.value);
    const selectedInGroup = groupValues.filter((value) => currentGroupSelections.includes(value));

    if (selectedInGroup.length === 0) return 'none';
    if (selectedInGroup.length === groupValues.length) return 'all';
    return 'partial';
  };

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen}>
      <PopoverTrigger asChild>
        <button
          className={multiSelectTrigger({ className })}
          data-placeholder={selectedOptions.length + selectedGroups.length === 0}
        >
          {getDisplayContent()}
          <FontAwesomeIcon className="size-4 shrink-0 opacity-50" icon={isOpen ? faChevronUp : faChevronDown} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="px-0 py-1.5" align="start">
        {groups.map((group) => {
          const selectionState = getGroupSelectionState(group);
          const hasOptions = group.options && group.options.length > 0;
          const isExpanded = expandedGroups.has(group.id);

          if (!hasOptions) {
            return (
              <FlexContainer className="gap-2 px-3 py-1" items="center" key={group.id}>
                <Checkbox
                  checked={selectionState === 'all'}
                  id={`group-${group.id}`}
                  onCheckedChange={() => handleGroupToggle(group)}
                />
                <Label className="flex-1 cursor-pointer select-none" htmlFor={`group-${group.id}`}>
                  {group.label}
                </Label>
              </FlexContainer>
            );
          }

          return (
            <div key={group.id}>
              <FlexContainer className="gap-2 px-3 py-1" items="center">
                <Checkbox
                  checked={selectionState !== 'none'}
                  id={`group-${group.id}`}
                  indeterminate={selectionState === 'partial'}
                  onCheckedChange={() => handleGroupToggle(group)}
                />
                <Label className="flex-1 cursor-pointer select-none" htmlFor={`group-${group.id}`}>
                  {group.label}
                </Label>
                <button
                  className="cursor-pointer p-1"
                  aria-label={`Toggle ${group.label}`}
                  onClick={() => toggleGroupExpanded(group.id)}
                  type="button"
                >
                  <FontAwesomeIcon
                    className="size-3 text-content-secondary"
                    icon={isExpanded ? faChevronDown : faChevronRight}
                  />
                </button>
              </FlexContainer>
              {isExpanded &&
                group.options!.map((option) => {
                  const currentGroupSelections = selectedValues[group.id] || [];
                  return (
                    <FlexContainer className="gap-2 pl-8 pr-3 py-2" items="center" key={option.id}>
                      <Checkbox
                        checked={currentGroupSelections.includes(option.value)}
                        id={`option-${option.id}`}
                        onCheckedChange={() => handleOptionToggle(option.value, group.id)}
                      />
                      <Label className="cursor-pointer select-none" htmlFor={`option-${option.id}`}>
                        {option.label}
                      </Label>
                    </FlexContainer>
                  );
                })}
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

export { MultiSelectWithGroups };
export type { MultiSelectGroup, MultiSelectOption, MultiSelectWithGroupsProps };
